import { useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { api } from "../../lib/api";

const INPUT = "mt-1.5 w-full rounded-lg border border-neutral-300 bg-white px-3.5 py-3 text-sm outline-none transition-[border-color,box-shadow] focus:border-[#0A8B85] focus:ring-2 focus:ring-[#0A8B85]/20";
const LABEL = "text-xs font-semibold uppercase tracking-wide text-neutral-500";
const FIELDS = [
  ["full_name", "Full name", "text", "Grace Achieng"],
  ["business_name", "Business name", "text", "Achieng Fresh Produce Ltd"],
  ["email", "Email address", "email", "you@business.com"],
  ["phone", "Phone number", "tel", "+254 7XX XXX XXX"],
];

export const DiscoveryForm = ({ config, onRefresh }) => {
  const [form, setForm] = useState({ full_name: "", business_name: "", email: "", phone: "", slot: "", format: "", brief: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const anyAvailable = config.slots.some((s) => s.available);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/public/discovery/book", form);
      window.location.assign(data.checkout_url);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Something went wrong — please try again.");
      if (ex.response?.status === 409) onRefresh();
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="discovery-form">
      {FIELDS.map(([k, label, type, ph]) => (
        <label key={k} className="block">
          <span className={LABEL}>{label} <span className="text-[#D4A83A]">*</span></span>
          <input type={type} required value={form[k]} onChange={set(k)} placeholder={ph} className={INPUT} data-testid={`discovery-input-${k}`} />
        </label>
      ))}
      <label className="block sm:col-span-2">
        <span className={LABEL}>Preferred slot <span className="text-[#D4A83A]">*</span></span>
        <select required value={form.slot} onChange={set("slot")} className={INPUT} data-testid="discovery-input-slot">
          <option value="">{anyAvailable ? "Select a slot" : "No slots currently available"}</option>
          {config.slots.map((s) => (
            <option key={s.key} value={s.key} disabled={!s.available} data-testid={`discovery-slot-${s.key}`}>
              {s.label} — {s.date_label}{s.available ? "" : " (unavailable — already booked)"}
            </option>
          ))}
        </select>
      </label>
      <fieldset className="sm:col-span-2" data-testid="discovery-format-group">
        <legend className={LABEL}>Session format <span className="text-[#D4A83A]">*</span></legend>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {config.formats.map((f) => (
            <label key={f.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 text-sm transition-[border-color,background-color] ${form.format === f.key ? "border-[#0A8B85] bg-[#0A8B85]/5" : "border-neutral-300 hover:border-neutral-400"}`}>
              <input type="radio" name="format" required value={f.key} checked={form.format === f.key} onChange={set("format")} className="mt-0.5 accent-[#0A8B85]" data-testid={`discovery-format-${f.key}`} />
              <span className="font-semibold text-[#0B2A28]">{f.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <label className="block sm:col-span-2">
        <span className={LABEL}>Briefly describe your business and what you want to work on in this session <span className="text-[#D4A83A]">*</span></span>
        <textarea required minLength={10} rows={4} value={form.brief} onChange={set("brief")} className={INPUT}
          placeholder="e.g. Agri-processing business, KES 40M revenue, raising working capital — I want clarity on the right structure and what funders will ask for."
          data-testid="discovery-input-brief" />
      </label>
      {err && <p className="text-sm text-red-600 sm:col-span-2" data-testid="discovery-form-error">{err}</p>}
      <button type="submit" disabled={busy || !anyAvailable}
        className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0A8B85] px-6 py-3.5 text-sm font-extrabold text-white transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#0C9F97] disabled:opacity-50 sm:col-span-2"
        data-testid="discovery-form-submit">
        {busy ? <><Loader2 size={16} className="animate-spin" /> Redirecting to secure payment…</> : <><Lock size={15} /> Continue to payment — KES {config.price_kes.toLocaleString()}</>}
      </button>
      <p className="text-center text-[11px] text-neutral-500 sm:col-span-2">Secure card or M-Pesa checkout via Paystack. Your booking is confirmed only once payment completes.</p>
    </form>
  );
};
